import { ActionModal } from '@kcftech/design-system';
import {
    ILocationResponse,
    IUserLocationChild,
} from '@kcftech/prometheustypescriptapiclient';
import { LocationDropdown } from 'components/dropdown/LocationDropdown';
import { Checkbox } from 'components/forms/Checkbox';
import { Label } from 'components/forms/Label';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useToggle } from 'custom_hooks/useToggle';
import { useMemo } from 'react';
import { Controller, RegisterOptions, useForm } from 'react-hook-form';

const locationRules: RegisterOptions = {
    required: {
        value: true,
        message: 'Please select a location',
    },
};

export type AddLocationToUserModalProps = Readonly<{
    onCreate: (l: ILocationResponse, isAdmin: boolean) => void;
    onClose: () => void;
    currentLocation?: IUserLocationChild;
    corporationId?: string;
}>;

type AddLocationToUserForm = Readonly<{
    location: ILocationResponse;
}>;

export function AddLocationToUserModal({
    onCreate,
    onClose,
    currentLocation,
    corporationId,
}: AddLocationToUserModalProps) {
    const isEditing = useMemo(() => currentLocation !== undefined, [currentLocation]);
    const [adminCheckboxChecked, toggleAdminCheckboxChecked] = useToggle();

    const {
        handleSubmit,
        formState: { errors, isSubmitted, isValid, isValidating, isSubmitting },
        control,
    } = useForm<AddLocationToUserForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const onSave = (data: AddLocationToUserForm) => {
        onCreate(data.location, adminCheckboxChecked);
    };

    return (
        <ActionModal
            onAction={handleSubmit(onSave)}
            actionable={(!isSubmitted || isValid) && !(isValidating || isSubmitting)}
            onClose={onClose}
            title={isEditing ? 'Edit Location Assignment' : 'Add Location Assignment'}
            actionButtonText="Save"
            closeButtonText="Cancel"
            height={400}
        >
            <>
                <ValidatableLabel
                    label="Location"
                    required={currentLocation === undefined}
                    className="pb-30"
                    error={errors.location}
                >
                    {currentLocation ? (
                        <span>{currentLocation.LocationName}</span>
                    ) : (
                        <Controller
                            control={control}
                            name="location"
                            rules={locationRules}
                            render={({ field: { value, onChange } }) => (
                                <LocationDropdown
                                    value={value}
                                    onChange={onChange}
                                    corporationId={corporationId}
                                />
                            )}
                        />
                    )}
                </ValidatableLabel>
                <Label label="Is Admin?">
                    <Checkbox
                        checked={adminCheckboxChecked}
                        onChange={toggleAdminCheckboxChecked}
                    />
                </Label>
            </>
        </ActionModal>
    );
}
